import { SAFE_URL_PROTOCOLS } from './constants';
import { renderRichText } from './rich-text';
import type { CtaAction, GuideInteractionMetadata, GuideStep, StepBlock } from './types';

interface StepCallbacks {
  /** `meta` identifica QUÉ botón se clickeó (analytics por botón). */
  onCtaClick: (action: CtaAction, url?: string, meta?: GuideInteractionMetadata) => void;
  onDismiss: () => void;
}

/**
 * Construye el subtree DOM de un step (título, contenido, imagen, CTA,
 * botón de cerrar) de forma segura.
 *
 * Reglas inmutables:
 *   - **Nunca** usa `innerHTML` ni `insertAdjacentHTML`.
 *   - Todo texto se asigna por `textContent` (escape HTML automático).
 *   - URLs (`imageUrl`, `style.ctaUrl`) pasan por `isSafeUrl` — solo
 *     `http(s):` permitido, rechaza `javascript:`, `data:`, etc.
 *   - Campos opcionales ausentes simplemente no pintan su nodo. Si una
 *     guía no trae `ctaText` no se pinta botón.
 *
 * El `doc` se inyecta para soportar tests con jsdom u otros documentos.
 */
export function buildStepContent(
  step: GuideStep,
  doc: Document,
  callbacks: StepCallbacks,
): HTMLElement {
  const container = doc.createElement('div');
  container.className = 'veo-guide-content';

  const blocks = normalizeBlocks(step);
  if (blocks) {
    renderBlocks(container, blocks, doc, callbacks);
  } else {
    renderLegacyStep(container, step, doc, callbacks);
  }

  // Botón "✕" siempre presente (esquina superior derecha).
  container.appendChild(createCloseButton(doc, () => callbacks.onDismiss()));

  return container;
}

/**
 * Nodos de CONTENIDO de un paso (títulos/textos/imágenes — SIN botones ni ✕):
 * respeta `blocks[]` (con rich text y full-bleed) o cae a los escalares
 * legacy. Lo usa el walkthrough, cuya navegación es propia — los bloques
 * `button` del paso se omiten porque atrás/siguiente/omitir los pinta el
 * chrome del tour.
 */
export function buildContentNodes(step: GuideStep, doc: Document): HTMLElement[] {
  const blocks = normalizeBlocks(step);
  if (blocks) {
    const nodes: HTMLElement[] = [];
    for (const { block, idx } of blocks) {
      if (block.type === 'button') continue;
      const node = buildContentBlock(block, doc);
      if (!node) continue;
      node.dataset.veoIdx = String(idx);
      nodes.push(node);
    }
    return nodes;
  }
  const nodes: HTMLElement[] = [];
  if (typeof step.imageUrl === 'string' && step.imageUrl && isSafeUrl(step.imageUrl)) {
    const img = doc.createElement('img');
    img.className = 'veo-guide-image';
    img.src = step.imageUrl;
    img.alt = typeof step.title === 'string' ? step.title : '';
    nodes.push(img);
  }
  if (typeof step.title === 'string' && step.title) {
    const heading = doc.createElement('h2');
    heading.className = 'veo-guide-title';
    heading.textContent = step.title;
    nodes.push(heading);
  }
  if (typeof step.content === 'string' && step.content) {
    const paragraph = doc.createElement('p');
    paragraph.className = 'veo-guide-text';
    paragraph.textContent = step.content;
    nodes.push(paragraph);
  }
  return nodes;
}

/** Render clásico: imagen + un título + un texto + un CTA (campos escalares). */
function renderLegacyStep(
  container: HTMLElement,
  step: GuideStep,
  doc: Document,
  callbacks: StepCallbacks,
): void {
  if (typeof step.imageUrl === 'string' && step.imageUrl && isSafeUrl(step.imageUrl)) {
    const img = doc.createElement('img');
    img.className = 'veo-guide-image';
    img.src = step.imageUrl;
    img.alt = typeof step.title === 'string' ? step.title : '';
    container.appendChild(img);
  }

  if (typeof step.title === 'string' && step.title) {
    const heading = doc.createElement('h2');
    heading.className = 'veo-guide-title';
    heading.textContent = step.title;
    container.appendChild(heading);
  }

  if (typeof step.content === 'string' && step.content) {
    const paragraph = doc.createElement('p');
    paragraph.className = 'veo-guide-text';
    paragraph.textContent = step.content;
    container.appendChild(paragraph);
  }

  const actions = doc.createElement('div');
  actions.className = 'veo-guide-actions';

  if (typeof step.ctaText === 'string' && step.ctaText) {
    const cta = doc.createElement('button');
    cta.type = 'button';
    cta.className = 'veo-guide-cta';
    cta.textContent = step.ctaText;
    cta.addEventListener('click', () => {
      const action: CtaAction = step.ctaAction ?? 'dismiss';
      const ctaUrl = readCtaUrl(step);
      callbacks.onCtaClick(action, ctaUrl);
    });
    actions.appendChild(cta);
  }

  container.appendChild(actions);
}

/**
 * Devuelve los bloques válidos del paso CON su índice original en
 * `step.blocks[]` (el drop-target del editor lo necesita: los bloques
 * filtrados u omitidos corren los índices del DOM, no los del modelo), o
 * `null` si no hay ninguno (usa el legacy).
 */
function normalizeBlocks(step: GuideStep): Array<{ block: StepBlock; idx: number }> | null {
  if (!Array.isArray(step.blocks) || step.blocks.length === 0) return null;
  const valid = step.blocks
    .map((block, idx) => ({ block, idx }))
    .filter((entry): entry is { block: StepBlock; idx: number } => {
      const b = entry.block;
      if (!b || typeof b !== 'object') return false;
      if (b.type === 'image') return typeof b.url === 'string' && isSafeUrl(b.url);
      return b.type === 'title' || b.type === 'text' || b.type === 'button';
    });
  return valid.length > 0 ? valid : null;
}

/**
 * Render por bloques: recorre en orden. Los botones consecutivos se agrupan en
 * una misma fila de acciones (para que queden lado a lado). Todo texto por
 * `textContent`; las imágenes por `isSafeUrl`.
 */
function renderBlocks(
  container: HTMLElement,
  blocks: Array<{ block: StepBlock; idx: number }>,
  doc: Document,
  callbacks: StepCallbacks,
): void {
  let i = 0;
  while (i < blocks.length) {
    const entry = blocks[i];
    if (entry?.block.type === 'button') {
      const actions = doc.createElement('div');
      actions.className = 'veo-guide-actions';
      // La fila agrupada lleva el índice del PRIMER botón.
      actions.dataset.veoIdx = String(entry.idx);
      // La alineación de un botón mueve la FILA (justify-content), no el botón
      // dentro de ella (align-self sería el eje vertical y no haría nada).
      let rowAlign: string | undefined;
      while (i < blocks.length && blocks[i]?.block.type === 'button') {
        const btnBlock = (blocks[i] as { block: StepBlock }).block;
        if (!rowAlign) {
          const a = (btnBlock.style as Record<string, unknown> | null | undefined)?.align;
          if (a === 'left' || a === 'center' || a === 'right') rowAlign = a;
        }
        actions.appendChild(buildButtonBlock(btnBlock, doc, callbacks));
        i++;
      }
      if (rowAlign) actions.style.justifyContent = BLOCK_ALIGN_SELF[rowAlign] as string;
      container.appendChild(actions);
      continue;
    }
    const node = entry ? buildContentBlock(entry.block, doc) : null;
    if (node && entry) {
      node.dataset.veoIdx = String(entry.idx);
      container.appendChild(node);
    }
    i++;
  }
}

export function buildContentBlock(block: StepBlock, doc: Document): HTMLElement | null {
  switch (block.type) {
    case 'title': {
      const el = doc.createElement('h2');
      el.className = 'veo-guide-title';
      el.textContent = typeof block.text === 'string' ? block.text : '';
      applyBlockStyle(el, block.style, 'text');
      return el;
    }
    case 'text': {
      // `div` (no `p`): el rich text puede contener sus propios <p>/<ul>.
      const el = doc.createElement('div');
      el.className = 'veo-guide-text';
      if (typeof block.html === 'string' && block.html) {
        el.appendChild(renderRichText(block.html, doc));
      } else {
        el.textContent = typeof block.text === 'string' ? block.text : '';
      }
      applyBlockStyle(el, block.style, 'text');
      return el;
    }
    case 'image': {
      if (typeof block.url !== 'string' || !isSafeUrl(block.url)) return null;
      const el = doc.createElement('img');
      el.className = 'veo-guide-image';
      el.src = block.url;
      el.alt = '';
      applyBlockStyle(el, block.style, 'image');
      return el;
    }
    default:
      return null;
  }
}

function buildButtonBlock(
  block: StepBlock,
  doc: Document,
  callbacks: StepCallbacks,
): HTMLButtonElement {
  const btn = doc.createElement('button');
  btn.type = 'button';
  btn.className = 'veo-guide-cta';
  btn.textContent = typeof block.text === 'string' && block.text ? block.text : 'OK';
  applyBlockStyle(btn, block.style, 'button');
  btn.addEventListener('click', () => {
    // Camino viejo (renderer clásico): solo maneja CtaAction. Las acciones
    // nuevas del modelo unificado (prev/submit-forms*) las maneja el renderer
    // unificado; acá degradan a `dismiss` (no aparecen en guías viejas).
    const raw = block.action ?? 'dismiss';
    const action: CtaAction = raw === 'next' || raw === 'url' || raw === 'guide' ? raw : 'dismiss';
    // El 2º argumento es "el destino" de la acción: la URL para `url`, el
    // guideId para `guide` (encadenado). Para el resto, undefined.
    const target =
      action === 'url' && typeof block.url === 'string' && isSafeUrl(block.url)
        ? block.url
        : action === 'guide' && typeof block.targetGuideId === 'string' && block.targetGuideId
          ? block.targetGuideId
          : undefined;
    const meta: GuideInteractionMetadata = {
      ...(typeof block.id === 'string' && block.id ? { buttonId: block.id } : {}),
      ...(typeof block.text === 'string' && block.text ? { buttonText: block.text } : {}),
    };
    callbacks.onCtaClick(action, target, Object.keys(meta).length ? meta : undefined);
  });
  return btn;
}

const BLOCK_ALIGN_SELF: Record<string, string> = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
};

function clampNum(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

/**
 * Aplica el estilo por-bloque como estilos INLINE (cada bloque es único, no
 * puede usar las CSS vars compartidas del host). Setear `el.style.*` es seguro:
 * el browser descarta valores inválidos (no hay inyección de CSS crudo).
 */
export function applyBlockStyle(
  el: HTMLElement,
  style: StepBlock['style'],
  kind: 'text' | 'image' | 'button',
): void {
  if (!style || typeof style !== 'object') return;
  const s = style as Record<string, unknown>;
  // Imagen "a sangre": ocupa todo el ancho de la tarjeta ignorando el padding
  // (hero image estilo anuncio). El CSS de la clase manda — se saltan las
  // props inline de tamaño/alineación que la contradirían.
  if (kind === 'image' && s.bleed === true) {
    el.classList.add('veo-guide-image--bleed');
    return;
  }
  const align = typeof s.align === 'string' ? s.align : null;
  if (align && (align === 'left' || align === 'center' || align === 'right')) {
    // text → text-align; image → align-self (se encoge y se ubica);
    // button → NO aquí (la fila de acciones maneja su justify-content).
    if (kind === 'text') el.style.textAlign = align;
    else if (kind === 'image') el.style.alignSelf = BLOCK_ALIGN_SELF[align] as string;
  }
  if (kind !== 'image' && typeof s.color === 'string') el.style.color = s.color;
  if (kind === 'text' && typeof s.fontSize === 'number' && Number.isFinite(s.fontSize)) {
    el.style.fontSize = `${clampNum(s.fontSize, 8, 72)}px`;
  }
  if (kind === 'image' && typeof s.width === 'number' && Number.isFinite(s.width)) {
    el.style.width = `${clampNum(s.width, 10, 100)}%`;
  }
  if (kind === 'image' && typeof s.radius === 'number' && Number.isFinite(s.radius)) {
    el.style.borderRadius = `${clampNum(s.radius, 0, 48)}px`;
  }
  if (typeof s.marginTop === 'number' && Number.isFinite(s.marginTop)) {
    el.style.marginTop = `${clampNum(s.marginTop, 0, 64)}px`;
  }
  if (typeof s.marginBottom === 'number' && Number.isFinite(s.marginBottom)) {
    el.style.marginBottom = `${clampNum(s.marginBottom, 0, 64)}px`;
  }

  // ── Estilos por-bloque extendidos (panel de estilos del editor) ──
  // Tipografía (texto/título/botón; no imagen).
  if (kind !== 'image') {
    if (typeof s.fontSize === 'number' && Number.isFinite(s.fontSize)) {
      el.style.fontSize = `${clampNum(s.fontSize, 8, 96)}px`;
    }
    if (typeof s.fontFamily === 'string' && s.fontFamily && s.fontFamily !== 'inherit') {
      el.style.fontFamily = s.fontFamily;
    }
    if (typeof s.fontWeight === 'string' && s.fontWeight) el.style.fontWeight = s.fontWeight;
    if (typeof s.textTransform === 'string' && s.textTransform && s.textTransform !== 'none') {
      el.style.textTransform = s.textTransform;
    }
    if (typeof s.lineHeight === 'number' && Number.isFinite(s.lineHeight)) {
      el.style.lineHeight = `${clampNum(s.lineHeight, 50, 300)}%`;
    }
    if (typeof s.letterSpacing === 'number' && Number.isFinite(s.letterSpacing)) {
      el.style.letterSpacing = `${clampNum(s.letterSpacing, -5, 20)}px`;
    }
  }
  // Fondo, borde, radio (todos los tipos).
  if (typeof s.background === 'string' && s.background) el.style.background = s.background;
  if (typeof s.borderWidth === 'number' && s.borderWidth > 0) {
    const bc = typeof s.borderColor === 'string' && s.borderColor ? s.borderColor : '#bbbbbb';
    el.style.border = `${clampNum(s.borderWidth, 0, 20)}px solid ${bc}`;
  }
  if (kind !== 'image' && typeof s.radius === 'number' && Number.isFinite(s.radius)) {
    el.style.borderRadius = `${clampNum(s.radius, 0, 64)}px`;
  }
  // Dimensiones explícitas (panel de estilos): ALTO para todos los tipos; ANCHO
  // en px solo para no-imagen — la imagen usa `width` como % (arriba), para no
  // romper guías existentes con hero images. Números → px, clamp 1..2000.
  if (typeof s.height === 'number' && Number.isFinite(s.height) && s.height > 0) {
    el.style.height = `${clampNum(s.height, 1, 2000)}px`;
  }
  if (kind !== 'image' && typeof s.width === 'number' && Number.isFinite(s.width) && s.width > 0) {
    el.style.width = `${clampNum(s.width, 1, 2000)}px`;
  }
  // Padding y margen — los 4 lados.
  for (const side of ['Top', 'Right', 'Bottom', 'Left'] as const) {
    const p = s[`padding${side}`];
    if (typeof p === 'number' && Number.isFinite(p)) {
      el.style.setProperty(`padding-${side.toLowerCase()}`, `${clampNum(p, 0, 128)}px`);
    }
    const m = s[`margin${side}`];
    if (typeof m === 'number' && Number.isFinite(m)) {
      el.style.setProperty(`margin-${side.toLowerCase()}`, `${clampNum(m, 0, 128)}px`);
    }
  }
}

/**
 * Botón "✕" de cerrar reutilizable. Universal: modal/banner/tooltip/inline (vía
 * `buildStepContent`), walkthrough, forms y guías custom lo usan, para que toda
 * guía tenga siempre una salida en la esquina superior derecha.
 *
 * `className` permite un estilo distinto por chrome (las custom usan
 * `veo-custom-close`, un botón visible sobre HTML arbitrario del cliente).
 */
export function createCloseButton(
  doc: Document,
  onClose: () => void,
  className = 'veo-guide-close',
): HTMLButtonElement {
  const closeBtn = doc.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = className;
  closeBtn.setAttribute('aria-label', 'Cerrar');
  closeBtn.textContent = '×';
  closeBtn.addEventListener('click', onClose);
  return closeBtn;
}

/**
 * Acepta solo `http:` y `https:`. Resuelve relativas contra la página
 * actual cuando hay `window`; en SSR (sin window) toma la URL como
 * absoluta. Rechaza cualquier protocolo no listado y URLs malformadas.
 */
export function isSafeUrl(url: string): boolean {
  if (typeof url !== 'string' || url.length === 0) return false;
  try {
    const base = typeof window !== 'undefined' ? window.location.href : 'http://localhost/';
    const parsed = new URL(url, base);
    return SAFE_URL_PROTOCOLS.includes(parsed.protocol);
  } catch {
    return false;
  }
}

/**
 * El backend no tiene aún un campo `ctaUrl` en el GuideStep — la URL
 * destino para `ctaAction === 'url'` se guarda como convención en
 * `style.ctaUrl`. Esta función la extrae validando que sea una URL
 * segura. Devuelve `undefined` si falta o no pasa el chequeo.
 */
function readCtaUrl(step: GuideStep): string | undefined {
  const candidate = step.style?.ctaUrl;
  if (typeof candidate !== 'string') return undefined;
  return isSafeUrl(candidate) ? candidate : undefined;
}
